import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function IdentityPage({ onConfirmed }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
      {/* BG glow */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(212,82,110,0.2) 0%, transparent 70%)' }} />

      {/* Corner ornaments */}
      {[{t:20,l:20,rx:1,ry:1},{t:20,r:20,rx:-1,ry:1},{b:20,l:20,rx:1,ry:-1},{b:20,r:20,rx:-1,ry:-1}].map((pos,i) => (
        <div key={i} style={{ position: 'fixed', top: pos.t, bottom: pos.b, left: pos.l, right: pos.r, opacity: 0.28, transform: `scaleX(${pos.rx}) scaleY(${pos.ry})`, pointerEvents: 'none' }}>
          <svg width="55" height="55" viewBox="0 0 55 55" fill="none"><path d="M5 5 L5 22 M5 5 L22 5" stroke="#C9A96E" strokeWidth="1.2" strokeLinecap="round"/><circle cx="5" cy="5" r="2" fill="#C9A96E"/><path d="M5 32 C5 18, 18 5, 32 5" stroke="#C9A96E" strokeWidth="0.6" strokeDasharray="2 3"/></svg>
        </div>
      ))}

      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22,1,0.36,1] }} style={{ textAlign: 'center', maxWidth: 520, position: 'relative', zIndex: 10 }}>
        {/* Rose */}
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
          style={{ marginBottom: 32, display: 'flex', justifyContent: 'center' }}>
          <span className="heart-beat" style={{ fontSize: '3.8rem' }}>🌹</span>
        </motion.div>

        {/* Tagline */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          style={{ fontFamily: "'Dancing Script', cursive", color: '#C9A96E', fontSize: '1.15rem', marginBottom: 16, letterSpacing: '0.05em' }}>
          a little something, just for you
        </motion.p>

        {/* Main question */}
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.9 }}
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.4rem, 6.5vw, 4.2rem)', fontWeight: 500, lineHeight: 1.15, color: '#FDF6F0', marginBottom: 14 }}>
          Are you{' '}
          <span style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#F2A7A7,#D4526E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Zainab Abdulkareem
          </span>
          ?
        </motion.h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.35 }} transition={{ delay: 0.85 }}
          style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.68rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#FDF6F0', marginBottom: 48 }}>
          please be honest
        </motion.p>

        {/* YES button */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
          style={{ display: 'flex', justifyContent: 'center' }}>
          <button onClick={() => setShowModal(true)}
            style={{
              padding: '15px 52px', borderRadius: 99, fontFamily: "'Lato', sans-serif",
              fontSize: '0.78rem', letterSpacing: '0.25em', textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #D4526E, #A0294A)',
              color: '#FDF6F0', border: 'none', cursor: 'pointer',
              boxShadow: '0 10px 36px rgba(212,82,110,0.48), inset 0 1px 0 rgba(255,255,255,0.15)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={e => e.target.style.transform='scale(1.04)'}
            onMouseLeave={e => e.target.style.transform='scale(1)'}>
            Yes, that's me ✨
          </button>
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.22 }} transition={{ delay: 1.5 }}
          style={{ marginTop: 28, fontSize: '0.72rem', color: '#FDF6F0', letterSpacing: '0.12em', fontFamily: "'Lato'" }}>
          (if you're not her, close this tab immediately)
        </motion.p>
      </motion.div>

      {/* MODAL */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(26,10,16,0.88)', backdropFilter: 'blur(14px)' }}>
            <motion.div
              initial={{ scale: 0.7, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              style={{
                maxWidth: 360, width: '100%', textAlign: 'center', padding: '48px 36px', borderRadius: 28,
                background: 'linear-gradient(145deg, #2E0F1C, #1A0A10)',
                border: '1px solid rgba(201,169,110,0.3)',
                boxShadow: '0 30px 80px rgba(212,82,110,0.28), inset 0 1px 0 rgba(201,169,110,0.18)',
              }}>
              {/* Icon ring */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', border: '1.5px solid rgba(201,169,110,0.45)', background: 'rgba(201,169,110,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' }}>
                  💌
                </div>
              </div>

              <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(253,246,240,0.35)', marginBottom: 8 }}>
                this was made for you by
              </p>

              <h2 className="gold-shimmer" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '4rem', fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>
                ARI
              </h2>

              <p style={{ fontFamily: "'Dancing Script', cursive", color: '#F2A7A7', fontSize: '1.05rem', marginBottom: 36 }}>
                with every bit of love he has for you
              </p>

              <button onClick={() => { setShowModal(false); onConfirmed(); }}
                style={{
                  padding: '11px 36px', borderRadius: 99, fontFamily: "'Lato', sans-serif",
                  fontSize: '0.68rem', letterSpacing: '0.25em', textTransform: 'uppercase',
                  background: 'transparent', border: '1px solid rgba(201,169,110,0.45)',
                  color: '#C9A96E', cursor: 'pointer', transition: 'all 0.25s',
                }}
                onMouseEnter={e => { e.target.style.background='rgba(201,169,110,0.1)'; e.target.style.transform='scale(1.04)'; }}
                onMouseLeave={e => { e.target.style.background='transparent'; e.target.style.transform='scale(1)'; }}>
                Continue →
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
